/* eslint-disable no-param-reassign */
/*
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import qrcode from 'qrcode';
import { TFunction } from 'i18next';
import Repos from '../../../infrastructure/repositories/Repos';
import { LoaInstanceResult } from '../../../infrastructure/repositories/ILoaInstanceRepository';
import {
  DEFAULT_EMAIL_DIGITS,
  DEFAULT_SMS_DIGITS,
  I18nNamespaces,
  ValidationErrorKey as ValErr
} from '../../../app/shared/utils/constants.util';
import {
  getHttpErrorStatusCode, isLoaRequiredError
} from '../../../app/shared/utils/errors.util';
import { requireUserId } from '../profileSlice';
import { AppThunk } from '../../../app/store';
import { showToast } from '../../../app/shared/AppSlice/AppSlice';
import {
  ISecuritySetup
} from '../../../domain/models/ISecuritySetup';
import { SecuritySetupType, AuthErrorKey } from '../../../domain/models/impl/SecuritySetup';
import { getSecretKeyLengthFromUri } from '../../../app/shared/utils/helpers.util';
import { getAllFactors } from '../../SecuritySetup/setupSecuritySlice';

export interface ProfileSelectedFactor {
  factorId: string;
  type: SecuritySetupType;
  digits?: number;
  loaLevel: number;
  data?: string;
}

export interface IProfileEnrollState {
  selectedFactor: ProfileSelectedFactor | undefined;
  actionLoading: boolean;
  error: string;
  validationError: string;
  qrCode: string;
  secretKey: string;
  isQrCodeLoading: boolean;
  enrollGeneration: number;
}

export const profileEnrollInitialState: IProfileEnrollState = {
  selectedFactor: undefined,
  actionLoading: false,
  error: '',
  validationError: '',
  qrCode: '',
  secretKey: '',
  isQrCodeLoading: false,
  enrollGeneration: 0
};

export const profileEnrollSlice = createSlice({
  name: 'profileEnroll',
  initialState: profileEnrollInitialState,
  reducers: {
    setSelectedFactor: (state, action: PayloadAction<ProfileSelectedFactor | undefined>) => {
      state.selectedFactor = action.payload;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setValidationError: (state, action: PayloadAction<string>) => {
      state.validationError = action.payload;
    },
    setQrCode: (state, action: PayloadAction<string>) => {
      state.qrCode = action.payload;
    },
    setSecretKey: (state, action: PayloadAction<string>) => {
      state.secretKey = action.payload;
    },
    setIsQrCodeLoading: (state, action: PayloadAction<boolean>) => {
      state.isQrCodeLoading = action.payload;
    },
    resetProfileEnrollState: (state): IProfileEnrollState => ({
      ...profileEnrollInitialState,
      enrollGeneration: state.enrollGeneration + 1
    })
  }
});

export const {
  setSelectedFactor,
  setActionLoading,
  setError,
  setValidationError,
  setQrCode,
  setSecretKey,
  setIsQrCodeLoading,
  resetProfileEnrollState
} = profileEnrollSlice.actions;

/** Check whether the enrollment session has been reset since the thunk started. */
const isStaleProfile = (gen: number, getState: () => any): boolean => (
  getState().profileEnroll.enrollGeneration !== gen
);

export type DeactivateResult = { status: 'success' | 'failed' | 'loaRequired' };

/**
 * Fetch a single-use LoA instance ID from the backend.
 * The returned instanceId is passed as a query param to the security setup popup
 * so the server can validate that the elevation was legitimately requested.
 */
export async function fetchLoaInstance(action: string): Promise<LoaInstanceResult> {
  return Repos.instance.loaInstanceRepository.fetchInstance(action);
}

/* ── Thunks ──────────────────────────────────────────────── */

export const profileEnrollAuthenticator = (
  factorData: Partial<ISecuritySetup>,
  t: TFunction
): AppThunk => async (dispatch, getState): Promise<void> => {
  const gen = getState().profileEnroll.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));
  dispatch(setIsQrCodeLoading(true));

  try {
    const userId = await dispatch(requireUserId() as any);

    const res = await Repos.instance.securitySetupRepository
      .create(factorData, { userId }) as Partial<ISecuritySetup>;

    if (!res || !res.id) {
      throw new Error('Empty Authenticator enroll response');
    }

    if (isStaleProfile(gen, getState)) return;

    const uri = res.options?.uri ?? '';
    const secret = res.options?.secret ?? '';
    const digits = uri ? getSecretKeyLengthFromUri(uri) : 0;
    const loaLevel = res?.level ?? 2;

    dispatch(setSelectedFactor({
      factorId: res.id!,
      type: res.type ?? SecuritySetupType.AUTHENTICATOR,
      digits,
      loaLevel
    }));

    try {
      if (uri) {
        const dataURL = await qrcode.toDataURL(uri);
        dispatch(setQrCode(dataURL));
      } else {
        dispatch(setQrCode(''));
      }
    } catch {
      dispatch(setQrCode(uri || ''));
    } finally {
      dispatch(setIsQrCodeLoading(false));
    }

    if (secret) {
      dispatch(setSecretKey(secret));
    } else {
      dispatch(setSecretKey(''));
    }
  } catch (err) {
    if (isStaleProfile(gen, getState)) return;
    dispatch(setIsQrCodeLoading(false));
    dispatch(setQrCode(''));
    dispatch(setSecretKey(''));

    const errorStatus = getHttpErrorStatusCode(err) ?? '500';
    if (errorStatus === 409) {
      dispatch(setError(AuthErrorKey.ResourceAlreadyExists));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceAlreadyExistsToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else if (errorStatus === 403) {
      dispatch(setError(AuthErrorKey.ResourceForbidden));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceForbiddenToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else {
      dispatch(setError(AuthErrorKey.DefaultErrorMessage));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    }
  } finally {
    if (!isStaleProfile(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const profileEnrollSMSFactor = (
  t: TFunction,
  {
    label,
    countryCode,
    nationalNumber,
    loaLevel
  }: {
    label: string;
    countryCode: string;
    nationalNumber: string;
    loaLevel: number;
  }
): AppThunk<Promise<string | undefined>> => async (dispatch, getState): Promise<string | undefined> => {
  const gen = getState().profileEnroll.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));

  try {
    const userId = await dispatch(requireUserId() as any);

    const payload: Partial<ISecuritySetup> = {
      type: SecuritySetupType.SMS,
      label,
      options: {
        phoneNumber: {
          countryCode,
          nationalNumber
        }
      }
    } as any;

    const res = await Repos.instance.securitySetupRepository.create(payload, { userId }) as Partial<ISecuritySetup>;

    if (!res?.id) {
      throw new Error('Empty SMS enroll response');
    }

    if (isStaleProfile(gen, getState)) return undefined;

    dispatch(setSelectedFactor({
      factorId: res.id!,
      type: SecuritySetupType.SMS,
      digits: (res.options as any)?.digits ?? DEFAULT_SMS_DIGITS,
      loaLevel: res.level ?? loaLevel,
      data: (res.options as any)?.phoneNumber ?? ''
    }));

    return res.id!;
  } catch (err) {
    if (isStaleProfile(gen, getState)) return undefined;
    const errorStatus = getHttpErrorStatusCode(err) ?? '500';
    if (errorStatus === 409) {
      dispatch(setError(AuthErrorKey.ResourceAlreadyExists));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceAlreadyExistsToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else if (errorStatus === 403) {
      dispatch(setError(AuthErrorKey.ResourceForbidden));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceForbiddenToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else {
      dispatch(setError(AuthErrorKey.DefaultSMSErrorMessage));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    }
    return undefined;
  } finally {
    if (!isStaleProfile(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const profileEnrollEmailFactor = (
  t: TFunction,
  { loaLevel }: { loaLevel: number }
): AppThunk<Promise<string | undefined>> => async (dispatch, getState) => {
  const gen = getState().profileEnroll.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));

  try {
    const userId = await dispatch(requireUserId() as any);

    const userEmail = getState().profile.profileDetails?.externalId;
    if (!userEmail) {
      dispatch(setError(AuthErrorKey.DefaultEmailErrorMessage));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.missingEmail', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
      return undefined;
    }

    const payload: Partial<ISecuritySetup> = { type: SecuritySetupType.EMAIL, email: userEmail } as any;

    const res = await Repos.instance.securitySetupRepository.create(payload, { userId }) as Partial<ISecuritySetup>;
    if (!res?.id) throw new Error('Empty Email enroll response');

    if (isStaleProfile(gen, getState)) return undefined;

    dispatch(setSelectedFactor({
      factorId: res.id!,
      type: SecuritySetupType.EMAIL,
      digits: (res.options as any)?.digits ?? DEFAULT_EMAIL_DIGITS,
      loaLevel: res.level ?? loaLevel
    }));

    try {
      await Repos.instance.securitySetupRepository.update({}, { userId, factorId: res.id!, action: 'challenge' });
    } catch {
      if (isStaleProfile(gen, getState)) return res.id!;
      dispatch(setError(AuthErrorKey.DefaultChallengeError));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.sendFailedToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
    }

    return res.id!;
  } catch {
    if (isStaleProfile(gen, getState)) return undefined;
    dispatch(setError(AuthErrorKey.DefaultEmailErrorMessage));
    dispatch(showToast({
      title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
      timeout: 0,
      kind: 'error',
      subtitle: ''
    }));
    return undefined;
  } finally {
    if (!isStaleProfile(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const profileChallengeFactor = (
  t: TFunction,
  {
    factorId,
    errorKey,
    toastKey
  }: {
    factorId: string;
    errorKey?: string;
    toastKey?: string;
  }
): AppThunk<Promise<boolean>> => async (dispatch, getState): Promise<boolean> => {
  const gen = getState().profileEnroll.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));
  try {
    const userId = await dispatch(requireUserId() as any);
    await Repos.instance.securitySetupRepository.update({}, { userId, factorId, action: 'challenge' });
    return true;
  } catch (err) {
    if (isStaleProfile(gen, getState)) return false;
    const errorStatus = getHttpErrorStatusCode(err) ?? '500';

    if (errorStatus === 404) {
      dispatch(resetProfileEnrollState());
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.otpSessionExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
      return false;
    }

    dispatch(setError(errorKey ?? AuthErrorKey.DefaultChallengeError));
    dispatch(showToast({
      title: t(toastKey ?? 'loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
      timeout: 0,
      kind: 'error',
      subtitle: ''
    }));
    return false;
  } finally {
    if (!isStaleProfile(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const profileActivateFactor = (
  t: TFunction,
  {
    factorId,
    code,
    action,
    label
  }: {
    factorId: string;
    code: string;
    action: 'activate' | 'verify';
    label?: string;
  }
): AppThunk<Promise<boolean>> => async (dispatch, getState): Promise<boolean> => {
  const gen = getState().profileEnroll.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));

  const selected = getState().profileEnroll.selectedFactor;
  const factorType = selected?.type;

  try {
    const userId = await dispatch(requireUserId() as any);
    const payload: Partial<ISecuritySetup> = { code, label };

    await Repos.instance.securitySetupRepository.update(
      payload,
      {
        userId, factorId, action, generateLoaProof: 'false'
      }
    );

    dispatch(showToast({
      title: t('loaEnrichment.common.messages.activated', { ns: I18nNamespaces.LOA_ENRICHMENT }),
      timeout: 0,
      kind: 'success',
      subtitle: ''
    }));

    await dispatch(getAllFactors(userId, undefined, 'ACTIVE'));

    return true;
  } catch (err) {
    if (isStaleProfile(gen, getState)) return false;
    const errorStatus = getHttpErrorStatusCode(err) ?? '500';

    if (factorType === SecuritySetupType.SMS) {
      if (errorStatus === 403) {
        dispatch(setValidationError(ValErr.InvalidSMSCode));
      } else if (errorStatus === 404) {
        dispatch(resetProfileEnrollState());
        dispatch(showToast({
          title: t('loaEnrichment.common.errors.otpSessionExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        }));
      } else {
        dispatch(setError(AuthErrorKey.DefaultErrorMessage));
        dispatch(showToast({
          title: t('loaEnrichment.factors.sms.toast.activationFailed', { ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        }));
      }
      return false;
    }

    if (factorType === SecuritySetupType.EMAIL) {
      if (errorStatus === 403) {
        dispatch(setValidationError(ValErr.InvalidEmailCode));
      } else if (errorStatus === 404) {
        dispatch(resetProfileEnrollState());
        dispatch(showToast({
          title: t('loaEnrichment.common.errors.otpSessionExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        }));
      } else {
        dispatch(showToast({
          title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
          timeout: 0,
          kind: 'error',
          subtitle: ''
        }));
      }
      return false;
    }

    // Authenticator
    if (errorStatus === 409) {
      dispatch(setError(AuthErrorKey.ResourceAlreadyExists));
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.resourceAlreadyExistsToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0 as number,
        kind: 'error',
        subtitle: ''
      }));
    } else if (errorStatus === 403) {
      dispatch(setValidationError(ValErr.OtpInvalid));
    } else if (errorStatus === 404) {
      dispatch(resetProfileEnrollState());
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.otpSessionExpiredToast', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
    } else {
      dispatch(showToast({
        title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));
    }
    return false;
  } finally {
    if (!isStaleProfile(gen, getState)) dispatch(setActionLoading(false));
  }
};

export const profileDeactivateFactor = (
  t: TFunction,
  factorId: string
): AppThunk<Promise<DeactivateResult>> => async (dispatch, getState): Promise<DeactivateResult> => {
  const gen = getState().profileEnroll.enrollGeneration;
  dispatch(setActionLoading(true));
  dispatch(setError(''));
  dispatch(setValidationError(''));
  try {
    const userId = await dispatch(requireUserId() as any);
    await Repos.instance.securitySetupRepository.delete('', { userId, factorId });
    if (isStaleProfile(gen, getState)) return { status: 'failed' };
    dispatch(setSelectedFactor(undefined));
    dispatch(showToast({
      title: t('loaEnrichment.common.messages.deactivated', { ns: I18nNamespaces.LOA_ENRICHMENT }),
      timeout: 0,
      kind: 'success',
      subtitle: ''
    }));
    await dispatch(getAllFactors(userId, undefined, 'ACTIVE'));
    return { status: 'success' };
  } catch (err) {
    if (isStaleProfile(gen, getState)) return { status: 'failed' };

    if (isLoaRequiredError(err)) {
      return { status: 'loaRequired' };
    }

    dispatch(setError(AuthErrorKey.DefaultErrorMessage));
    dispatch(showToast({
      title: t('loaEnrichment.common.errors.default', { ns: I18nNamespaces.LOA_ENRICHMENT }),
      timeout: 0,
      kind: 'error',
      subtitle: ''
    }));
    return { status: 'failed' };
  } finally {
    if (!isStaleProfile(gen, getState)) dispatch(setActionLoading(false));
  }
};

export default profileEnrollSlice.reducer;
